import React, { useEffect, useRef } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useMagnesiumOxideStore, CameraView } from '../store/useMagnesiumOxideStore';

interface ViewPreset {
  position: [number, number, number];
  target: [number, number, number];
}

const VIEW_PRESETS: Record<CameraView, ViewPreset> = {
  default:    { position: [0, 1.5, 4],    target: [0, 0.5, -0.1] },
  top:        { position: [0, 4.0, 0.4],  target: [0, 0,   -0.2] },
  side:       { position: [3.4, 1.1, 0],  target: [0, 0.3, -0.2] },
  closeup:    { position: [0, 0.9, 1.4],  target: [0, 0.5, -0.2] },
  workbench:  { position: [-1.7, 1.6, 2.4], target: [-0.4, 0, 0.2] },
};

interface Props {
  controlsRef: React.MutableRefObject<any>;
}

export function CameraController({ controlsRef }: Props) {
  const { camera } = useThree();
  const cameraView = useMagnesiumOxideStore(s => s.cameraView);
  const cameraVersion = useMagnesiumOxideStore(s => s.cameraVersion);
  const animatingRef = useRef(false);
  const startPosRef = useRef(new THREE.Vector3());
  const endPosRef = useRef(new THREE.Vector3());
  const startTargetRef = useRef(new THREE.Vector3());
  const endTargetRef = useRef(new THREE.Vector3());
  const tRef = useRef(0);

  useEffect(() => {
    const preset = VIEW_PRESETS[cameraView] ?? VIEW_PRESETS.default;
    startPosRef.current.copy(camera.position);
    endPosRef.current.set(...preset.position);
    if (controlsRef.current) {
      startTargetRef.current.copy(controlsRef.current.target);
    } else {
      startTargetRef.current.set(0, 0.5, -0.1);
    }
    endTargetRef.current.set(...preset.target);
    tRef.current = 0;
    animatingRef.current = true;
  }, [cameraVersion, cameraView, camera, controlsRef]);

  useFrame((_, delta) => {
    if (!animatingRef.current) return;
    tRef.current = Math.min(1, tRef.current + delta * 1.6);
    const t = tRef.current;
    const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    camera.position.lerpVectors(startPosRef.current, endPosRef.current, eased);
    if (controlsRef.current) {
      controlsRef.current.target.lerpVectors(startTargetRef.current, endTargetRef.current, eased);
      controlsRef.current.update();
    }
    if (t >= 1) animatingRef.current = false;
  });

  return null;
}

export default CameraController;
